import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Grid, TextField, MenuItem, Button,
  Typography, Alert, Divider, Chip, Table, TableHead, TableRow,
  TableCell, TableBody, Checkbox
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import PageHeader from '../common/PageHeader';

export default function CreateRemittance() {
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [forexDeals, setForexDeals] = useState([]);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    remittance_number: '', remittance_date: today, beneficiary_id: '',
    deal_id: '', currency: 'USD', amount: '', inr_amount: '', exchange_rate: '',
    bank_reference: '', kotak_reference: '', purpose_code: 'P0101',
    purpose_description: 'Advance Import Remittance(AIR)', notes: ''
  });

  useEffect(() => {
    Promise.all([
      api.get('/beneficiaries'),
      api.get('/forex-deals/active'),
      api.get('/invoices')
    ]).then(([b, fd, inv]) => {
      setBeneficiaries(b.data.data);
      setForexDeals(fd.data.data);
      setPendingInvoices(inv.data.data.filter(i => ['draft', 'pending', 'booked'].includes(i.status)));
    });
  }, []);

  const beneficiaryInvoices = pendingInvoices.filter(i =>
    form.beneficiary_id ? i.beneficiary_id === parseInt(form.beneficiary_id) : true
  );

  const toggleInvoice = (inv) => {
    setSelectedInvoices(prev =>
      prev.find(i => i.id === inv.id)
        ? prev.filter(i => i.id !== inv.id)
        : [...prev, { id: inv.id, amount: inv.total_amount }]
    );
  };

  const selectedTotal = selectedInvoices.reduce((s, i) => s + parseFloat(i.amount || 0), 0);

  useEffect(() => {
    if (selectedTotal > 0 && form.amount === '') {
      setForm(f => ({ ...f, amount: selectedTotal.toFixed(2) }));
    }
  }, [selectedTotal]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.remittance_number || !form.beneficiary_id || !form.amount) {
      setError('Reference number, beneficiary and amount are required');
      return;
    }
    setLoading(true);
    try {
      await api.post('/remittances', { ...form, invoice_ids: selectedInvoices });
      navigate('/remittances');
    } catch (err) {
      setError(err.response?.data?.message || 'Error creating remittance');
    } finally { setLoading(false); }
  };

  const f = (field) => ({ value: form[field], onChange: (e) => setForm({ ...form, [field]: e.target.value }) });

  return (
    <Box component="form" onSubmit={handleSave}>
      <PageHeader title="New Outward Remittance" subtitle="Record a forex remittance to supplier"
        breadcrumbs={[{ label: 'Remittances', path: '/remittances' }, { label: 'New Remittance' }]}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => navigate('/remittances')}>Cancel</Button>
            <Button type="submit" variant="contained" startIcon={<Save />} disabled={loading}>
              {loading ? 'Saving...' : 'Save Remittance'}
            </Button>
          </Box>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={7}>
          <Card sx={{ mb: 2.5 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Remittance Details</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField label="Portal Ref. # (Kotak FT Number) *" fullWidth {...f('remittance_number')}
                    placeholder="e.g. FT26020006459389" />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Remittance Date *" type="date" fullWidth {...f('remittance_date')} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Beneficiary *" select fullWidth value={form.beneficiary_id}
                    onChange={(e) => setForm({ ...form, beneficiary_id: e.target.value, amount: '' })}>
                    {beneficiaries.map(b => <MenuItem key={b.id} value={b.id}>{b.name} ({b.country} · {b.currency})</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={4}>
                  <TextField label="Currency" select fullWidth {...f('currency')}>
                    {['USD', 'EUR', 'GBP', 'CNY', 'AED', 'SGD'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={4}>
                  <TextField label="Amount *" type="number" fullWidth {...f('amount')} inputProps={{ step: '0.01' }} />
                </Grid>
                <Grid item xs={4}>
                  <TextField label="Exchange Rate" type="number" fullWidth {...f('exchange_rate')}
                    placeholder="e.g. 86.50"
                    onChange={(e) => setForm({ ...form, exchange_rate: e.target.value, inr_amount: e.target.value && form.amount ? (parseFloat(form.amount) * parseFloat(e.target.value)).toFixed(2) : '' })} />
                </Grid>
                {form.inr_amount && (
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, bgcolor: '#e8eaf6', borderRadius: 2 }}>
                      <Typography variant="caption">INR Value: <strong>₹{parseFloat(form.inr_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</strong></Typography>
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField label="Forex Deal (Kotak)" select fullWidth value={form.deal_id}
                    onChange={(e) => setForm({ ...form, deal_id: e.target.value })}>
                    <MenuItem value="">None / Spot Rate</MenuItem>
                    {forexDeals.map(d => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.deal_id} · {d.currency} {parseFloat(d.amount).toLocaleString()} @ {d.rate} ({d.status})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Kotak Master Reference" fullWidth {...f('kotak_reference')}
                    placeholder="e.g. 4635AIR261423425" />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Bank Reference (FT No.)" fullWidth {...f('bank_reference')} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Purpose Code" fullWidth {...f('purpose_code')} placeholder="e.g. P0101" />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Remittance Type" fullWidth {...f('purpose_description')} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Notes" fullWidth multiline rows={2} {...f('notes')} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Link Invoices (Optional)</Typography>
              <Typography variant="caption" color="text.secondary">
                Select pending invoices covered by this remittance
              </Typography>
              {beneficiaryInvoices.length === 0 ? (
                <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography variant="body2">No pending invoices{form.beneficiary_id ? ' for this beneficiary' : ''}</Typography>
                </Box>
              ) : (
                <Table size="small" sx={{ mt: 1 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox"></TableCell>
                      <TableCell>Invoice</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {beneficiaryInvoices.map(inv => (
                      <TableRow key={inv.id} hover sx={{ cursor: 'pointer' }} onClick={() => toggleInvoice(inv)}>
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={Boolean(selectedInvoices.find(i => i.id === inv.id))} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" fontWeight={600}>{inv.invoice_number}</Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">{inv.invoice_date}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" fontWeight={600}>{inv.currency} {parseFloat(inv.total_amount).toLocaleString()}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {selectedInvoices.length > 0 && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: '#e8eaf6', borderRadius: 2 }}>
                  <Typography variant="caption">Selected: {selectedInvoices.length} invoices</Typography>
                  <br />
                  <Typography variant="subtitle2" color="primary">Total: {form.currency} {selectedTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
